import { useEffect, useState } from 'react'
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid'
import { Combobox, ComboboxButton, ComboboxInput, ComboboxOption, ComboboxOptions, Label } from '@headlessui/react'
import { useSearchParams } from "@remix-run/react";
import { api } from '#convex/_generated/api.js';
import { useQuery } from 'convex/react';
import { Doc, Id } from '#convex/_generated/dataModel.js';
import { inputStyle } from '#app/styles/styles.js';
import SubdivisionName from '../SubdivisionName';

function classNames(...classes: (string | boolean)[]) {
  return classes.filter(Boolean).join(' ')
}

type SubdivisionSelectorProps = {
  selectedSubdivision: Doc<"Subdivision"> | null,
  setSelectedSubdivision: (user: Doc<"Subdivision"> | null) => void
  regionId: Id<"Region">
  subdivisionId?: Id<"Subdivision"> | null
}

export default function SubdivisionSelector({selectedSubdivision, setSelectedSubdivision, regionId, subdivisionId}: SubdivisionSelectorProps) {
  const [query, setQuery] = useState('')
  const subdivisions = useQuery(api.subdivision.getSubdivisionsForRegion, {regionId: regionId as Id<"Region">})
  const [searchParams, setSearchParams] = useSearchParams();
  
  useEffect(() => {
    if(subdivisionId != null) {
      const subdivision = subdivisions?.find((sub) => sub._id === subdivisionId);
      if(subdivision != null) {
        setSelectedSubdivision(subdivision);
      }
    }
  }, [subdivisionId]);
  
  if(subdivisions == null) {
    return <p className="italic m-10 text-color-gray">Loading...</p>
  }
  
  const filteredSubdivisions =
  query === '' ? subdivisions : subdivisions.filter((sub) => 
    sub.name.toLowerCase().includes(query.toLowerCase())
)

if(subdivisionId != null) {
  return <div className="font-bold uppercase"><SubdivisionName subdivision={subdivisionId} /></div>
}

  return (
    <div className="w-full text-left">
      <Combobox as="div" value={selectedSubdivision} onChange={(sub) => {
        setSelectedSubdivision(sub)
        const newSearchParams = new URLSearchParams(searchParams);
        if (sub?._id) {
          newSearchParams.set('subdivision', sub._id);
        } else {
          newSearchParams.delete('subdivision');
        }
        setSearchParams(newSearchParams);
      }}>
        <Label className="block text-sm font-medium leading-6 text-gray-900">Select Subdivision</Label>
        <input hidden name="subdivisionID" id="subdivisionID" defaultValue={selectedSubdivision ? selectedSubdivision._id : ""} />
        <div className="relative">
          <ComboboxInput
            className={inputStyle}
            onChange={(event) => setQuery(event.target.value)}
            displayValue={(sub: Doc<"Subdivision"> | string) => {
              if (typeof sub === 'object' && sub != null) {
                return sub?.name;
              } else {
                return "";
              }
            }}
          />
          <ComboboxButton className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
            <ChevronUpDownIcon className="h-5 w-5 text-gray" aria-hidden="true" />
          </ComboboxButton>
            <ComboboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
              {filteredSubdivisions.sort((a, b) => a?.name.localeCompare(b?.name)).map((sub) => (
                  <ComboboxOption
                    key={sub._id}
                    value={sub}
                    className={({ active }) =>
                      classNames(
                        'relative cursor-default select-none py-2 pl-3 pr-9',
                        active ? 'bg-blue text-white' : 'text-booBlack'
                      )
                    }
                  >
                    {({ active, selected }) => (
                      <>
                        <span className={classNames('block truncate', selected && 'font-semibold')}>{sub.name}</span>
    
                        {selected && (
                          <span
                            className={classNames(
                              'absolute inset-y-0 right-0 flex items-center pr-4',
                              active ? 'text-white' : 'text-blue'
                            )}
                          >
                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                          </span>
                        )}
                      </>
                    )}
                  </ComboboxOption>
                ))}
            </ComboboxOptions>
        </div>
      </Combobox>
    </div>
  )
}
